import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import AGENT_CREATION_TEMPLATES, {
  AGENT_CREATION_TEMPLATE_CATEGORY_ORDER,
} from "./agentConfigCreationTemplates";
import AgentTemplateSprite from "./AgentTemplateSprite";

const GROUPED_AGENT_CREATION_TEMPLATES = AGENT_CREATION_TEMPLATE_CATEGORY_ORDER.map(
  (category) => ({
    category,
    templates: AGENT_CREATION_TEMPLATES.filter(
      (template) => template.category === category
    ),
  })
).filter((group) => group.templates.length > 0);

export function AgentConfigCreationPathChooser({
  onSelectManual,
  onSelectAi,
  saving,
}) {
  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 680, py: 4, px: 3, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "text.primary",
            mb: 1,
          }}
        >
          How should we configure this agent?
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary", mb: 3 }}>
          Choose whether you want to configure everything yourself or have AI
          configure itself from your natural-language requirements.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onSelectManual}
            disabled={saving}
            sx={creationPathButtonSx}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Configure manually
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
              >
                Use the full configuration UI for name, prompt, tools,
                knowledge, and policies.
              </Typography>
            </Box>
          </Button>
          <Button
            variant="outlined"
            onClick={onSelectAi}
            disabled={saving}
            sx={creationPathButtonSx}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Let AI self-configure
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
              >
                Describe your requirements in natural language. The agent will
                be created with self-configuration awareness and edit enabled.
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function AgentConfigAiCreationPanel({
  aiRequirements,
  setAiRequirements,
  creationError,
  setCreationError,
  saving,
  stageLabel,
}) {
  return (
    <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          py: 3,
          px: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "text.primary",
            mb: 1,
          }}
        >
          Describe your instructions and requirements
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}>
          We will create the agent with self-configuration awareness and edit
          enabled, then automatically start the first chat with this message.
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={10}
          value={aiRequirements}
          onChange={(e) => {
            setAiRequirements(e.target.value);
            if (creationError) setCreationError("");
          }}
          placeholder="Example: Create an operations assistant that triages support requests, updates task status, and asks clarifying questions before escalating."
          disabled={saving}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": {
              height: "100%",
              alignItems: "flex-start",
            },
            "& textarea": { height: "100% !important" },
          }}
        />
        {creationError && (
          <Typography sx={{ fontSize: "12px", color: "error.main", mt: 1.5 }}>
            {creationError}
          </Typography>
        )}
        {saving && (
          <Box
            sx={{
              mt: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              py: 1,
              borderRadius: "8px",
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CircularProgress size={16} sx={{ color: "text.secondary" }} />
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              {stageLabel}
            </Typography>
          </Box>
        )}
      </Box>
      <AgentCreationTemplatePanel
        onSelectTemplate={(template) => {
          setAiRequirements(template.prompt);
          if (creationError) setCreationError("");
        }}
        disabled={saving}
      />
    </Box>
  );
}

function AgentCreationTemplatePanel({ onSelectTemplate, disabled }) {
  return (
    <Box
      sx={{
        flex: "0 0 42%",
        minWidth: 380,
        maxWidth: 620,
        borderLeft: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundColor: "action.hover",
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>
          Start from a template
        </Typography>
        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5, lineHeight: 1.5 }}
        >
          Pick a use case to prefill your instructions. Edit it however you like.
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          pb: 2,
        }}
      >
        {GROUPED_AGENT_CREATION_TEMPLATES.map((group) => (
          <Box key={group.category} sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1,
                px: 0.5,
              }}
            >
              {group.category}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 1,
                alignContent: "start",
              }}
            >
              {group.templates.map((template) => (
                <Button
                  key={template.id}
                  className="agent-template-card"
                  variant="outlined"
                  onClick={() => onSelectTemplate(template)}
                  disabled={disabled}
                  sx={templateCardSx}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                    }}
                  >
                    <AgentTemplateSprite
                      seed={template.id}
                      groupSeed={group.category}
                      size={36}
                    />
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                        {template.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "text.secondary",
                          mt: 0.5,
                          whiteSpace: "normal",
                        }}
                      >
                        {template.shortDescription}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const templateCardSx = {
  justifyContent: "flex-start",
  alignItems: "flex-start",
  textAlign: "left",
  textTransform: "none",
  borderRadius: "10px",
  borderColor: "divider",
  color: "text.primary",
  backgroundColor: "background.paper",
  width: "100%",
  p: 1.5,
  "&:hover": {
    backgroundColor: "background.paper",
    borderColor: "text.disabled",
    boxShadow: "6px 6px 0px 0px rgba(0, 0, 0, 0.25)",
  },
};

const creationPathButtonSx = {
  justifyContent: "flex-start",
  alignItems: "flex-start",
  textAlign: "left",
  textTransform: "none",
  borderRadius: "10px",
  borderColor: "divider",
  color: "text.primary",
  p: 2,
};
